import React, { useEffect } from 'react'

import { useEditDoor } from '../hooks/useEditDoor'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box, GridItem } from '@chakra-ui/react'

import {
  Button,
  FormInputControl,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceModalSection } from '../../components/EditDeviceModalSection'
import { FourColumnGrid } from '../../components/FourColumnGrid'
import { SectionHeader } from '../../components/SectionHeader'
import { TwoColumnGrid } from '../../components/TwoColumnGrid'
import { FormDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'

interface EditDoorFormIProps {
  deviceId: string
  onLoadingChange: (loading: boolean) => void
}

export const EditDoorForm = ({
  deviceId,
  onLoadingChange,
}: EditDoorFormIProps) => {
  const {
    handleSubmit,
    errors,
    register,
    control,
    formState,
    setValue,
    trigger,
  } = useForm<FormDataTypeI>({
    mode: 'onChange',
  })
  const { shouldEnableUpdateDevice } = usePermissions()
  const { isValid } = formState
  const {
    deviceData,
    isLoading,
    floorsOptions,
    isUpdateDeviceLoading,
    isUpdateDoorLoading,
    updateDoor,
  } = useEditDoor(deviceId)

  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<FormDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location.value,
      manufacturerName: values.manufacturerName,
    }

    try {
      await updateDoor(formData, values.externalId)
      showSuccess('Device updated.')
    } catch (e) {
      showError()
    }
  }

  useEffect(() => {
    if (!isLoading) {
      setValue('name', deviceData?.device?.name, { shouldValidate: true })
      setValue(
        'location',
        floorsOptions?.find((i) => i.value === deviceData?.device?.floor?.id),
        {
          shouldValidate: true,
        }
      )
      setValue('externalId', deviceData?.device?.door?.externalId)
      setValue('manufacturerName', deviceData?.device?.manufacturerName)
      trigger(['name'])
    }
  }, [isLoading, floorsOptions])

  useEffect(() => {
    // Update parent component's loading state
    onLoadingChange(isLoading)
  }, [deviceData, isLoading])

  return (
    <Box>
      {!deviceData || isLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection mb={6}>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle='Configure the basic device information and connections settings for this device '
                title='Device Details & Connection'
              />
            </GridItem>
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!isValid || !shouldEnableUpdateDevice}
                isLoading={isUpdateDeviceLoading || isUpdateDoorLoading}
                loadingText='Saving Changes'
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>

          <Box>
            <form>
              <FourColumnGrid>
                <GridItem>
                  <FormInputControl
                    data-testid='devicesPage_editDeviceModal_name'
                    errorMessage={errors.name && errors.name.message}
                    id='name'
                    inputRef={register({
                      required: 'Device Name is required',
                    })}
                    isInvalid={!!errors.name}
                    label='Device Name'
                    placeholder='Enter the Device Name'
                  />
                </GridItem>
                <GridItem>
                  <FormInputSelectControl
                    control={control}
                    data-testid='devicesPage_editDeviceModal_location'
                    defaultValue={null}
                    errorMessage={
                      !!errors?.location && 'Select a device location'
                    }
                    id='location'
                    isClearable
                    isDisabled={isLoading || isPlaced(deviceData?.device)}
                    isInvalid={!!errors?.location}
                    label='Device Location'
                    options={floorsOptions}
                    placeholder='Select Device Location'
                    rules={{ required: true }}
                    tooltipText={
                      isPlaced(deviceData?.device) &&
                      'Device must not be placed to update device location'
                    }
                  />
                </GridItem>
                <GridItem>
                  <FormInputControl
                    data-testid='devicesPage_editDeviceModal_manufacturerName'
                    id='manufacturerName'
                    inputRef={register()}
                    label='Device Manufacturer'
                    placeholder='Enter Name'
                  />
                </GridItem>
                <GridItem>
                  <FormInputControl
                    data-testid='devicesPage_editDeviceModal_externalId'
                    errorMessage={errors.externalId?.message}
                    id='externalId'
                    inputRef={register()}
                    label='External ID'
                    placeholder='Enter External ID'
                    tooltipText='This associates the device with a camera'
                  />
                </GridItem>
              </FourColumnGrid>
            </form>
          </Box>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
